import LaunchOutlinedIcon from '@mui/icons-material/LaunchOutlined';
import { Link } from 'react-router-dom';
import Tooltip from '../common/Tooltip';
import { ActivationBadge, Spinner } from './RetentionScoreCells.jsx';
import { daysColorClass, formatDayAge } from '../../utils/activityAge';

// De statussen die een beheerder kan zetten, in dezelfde volgorde en met
// dezelfde woorden als de keuzelijst in het projectenoverzicht.
const STATUS_OPTIONS = [
    { value: 'active', label: 'Actief' },
    { value: 'inactive', label: 'Inactief' },
    { value: 'archived', label: 'Gearchiveerd' },
    { value: 'demo', label: 'Demo' },
];

const wdToWeeks = (n) => Math.round(n / 5);

// `businessDaysSince` telt net als `daysSince` negatief voor een datum die nog
// moet komen; formatDayAge maakt daar 'over 2 wd' van in plaats van '-2 wd'.
// AI-CONTEXT: See docs/pitfalls.md#ui-negative-day-count-reads-as-days-ago
const formatLastLogin = (days) => {
    if (days === null || days === undefined) return 'nog nooit';
    return formatDayAge(days, ' wd');
};

// '…' en '—' zeggen niet hetzelfde: het eerste is 'weet ik nog niet', het
// tweede 'er is er geen'. Een streepje tijdens het laden is een bewering aan de
// lezer die op dat moment nergens op gebaseerd is.
// AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
const UNKNOWN = '…';

const formatAge = (days, loading) => {
    if (days === null || days === undefined) return loading ? UNKNOWN : '—';
    if (days < 5) return `${days} wd`;
    return `${wdToWeeks(days)} wk`;
};

/**
 * De feitenregel bovenaan het tabblad 'Status & gezondheid': dezelfde gegevens
 * die in het projectenoverzicht als eerste kolommen naast elkaar staan, maar
 * dan voor één project.
 *
 * De strip toont ze; hij schrijft niet zelf. De pagina eromheen doet de write
 * via een Cloud Function, want CompagnyMeta van een ánder project is
 * cross-tenant en de Firestore-regels blokkeren dat in de browser.
 *
 * @param {object} props
 * @param {string} [props.status] - CompagnyMeta.Status ('active' / 'inactive' / 'archived' / 'demo')
 * @param {number | null} [props.daysSinceCreated] - werkdagen sinds het project bestaat
 * @param {number | null} [props.lastLoginBusinessDaysAgo] - werkdagen sinds de laatste login
 * @param {string | null} [props.projectUrl] - link naar de omgeving van het project
 * @param {boolean} [props.loading] - het project is nog niet geladen
 * @param {boolean} [props.scoreLoading] - de retentiescore (en dus de leeftijd) is nog onderweg
 * @param {boolean} [props.parentsLoading] - de namen van de portfolio's zijn nog onderweg
 * @param {Array<{id: string, name: string, to: string}>} [props.parents] - portfolio's waar dit project onder hangt, met het pad naar hun eigen detailpagina
 * @param {React.ReactNode} [props.sectorsCell] - de sectorcel; de pagina levert 'm omdat die de write doet
 * @param {((status: string) => void) | null} [props.onStatusChange] - zetten mag; zonder deze functie is de status alleen te lezen
 * @param {(() => void) | null} [props.onStartSession] - een superadmin-sessie aanvragen voor deze omgeving
 */
const ProjectMetaStrip = ({
    status,
    daysSinceCreated,
    lastLoginBusinessDaysAgo,
    projectUrl,
    loading,
    scoreLoading = false,
    parents = [],
    parentsLoading = false,
    sectorsCell = null,
    onStatusChange = null,
    onStartSession = null,
}) => {
    if (loading) {
        return (
            <div className="project-meta-strip">
                <Spinner />
            </div>
        );
    }

    const lastLoginText = formatLastLogin(lastLoginBusinessDaysAgo);
    const ageText = formatAge(daysSinceCreated, scoreLoading);

    return (
        <div className="project-meta-strip">

            <span className="project-meta-strip__item">
                <span className="project-meta-strip__label">Status</span>
                {onStatusChange
                    ? (
                        <select
                            className="project-meta-strip__select"
                            aria-label="Status van dit project"
                            value={status || ''}
                            onChange={(e) => onStatusChange(e.target.value)}
                        >
                            <option value="">—</option>
                            {STATUS_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    )
                    : status
                        ? <ActivationBadge status={status} />
                        : <span>—</span>}
            </span>

            <span className="project-meta-strip__separator" aria-hidden="true">·</span>

            <span className="project-meta-strip__item">
                <span className="project-meta-strip__label">Bestaat sinds</span>
                <span>{ageText}</span>
            </span>

            <span className="project-meta-strip__separator" aria-hidden="true">·</span>

            <span className="project-meta-strip__item">
                <span className="project-meta-strip__label">Laatste login</span>
                <span className={lastLoginBusinessDaysAgo !== null ? daysColorClass(lastLoginBusinessDaysAgo) : ''}>
                    {lastLoginText === 'nog nooit' ? 'nog nooit' : lastLoginText}
                </span>
            </span>

            <span className="project-meta-strip__separator" aria-hidden="true">·</span>

            <span className="project-meta-strip__item">
                <span className="project-meta-strip__label">Portfolio</span>
                {parentsLoading && parents.length === 0
                    ? <span>{UNKNOWN}</span>
                    : parents.length > 0
                    ? (
                        <span className="portfolio-tags">
                            {parents.map((parent) => (
                                // Een portfolio is zelf ook een project, dus de tag brengt je
                                // naar zijn eigen detailpagina. Zonder link is de wijzende
                                // cursor van .portfolio-tag een loze belofte.
                                <Link className="portfolio-tag" key={parent.id} to={parent.to}>{parent.name}</Link>
                            ))}
                        </span>
                    )
                    : <span>—</span>}
            </span>

            <span className="project-meta-strip__separator" aria-hidden="true">·</span>

            <span className="project-meta-strip__item">
                <span className="project-meta-strip__label">Sector</span>
                {sectorsCell || <span>—</span>}
            </span>

            <span className="project-meta-strip__separator" aria-hidden="true">·</span>

            <span className="project-meta-strip__item">
                <span className="project-meta-strip__label">Omgeving</span>
                {projectUrl
                    ? (
                        <Tooltip content="Open project-omgeving" top="-35px">
                            <a href={projectUrl} target="_blank" rel="noreferrer"><LaunchOutlinedIcon /></a>
                        </Tooltip>
                    )
                    : <span>—</span>}
                {onStartSession && (
                    <button
                        type="button"
                        className="superadmin-open-tenant-button"
                        onClick={onStartSession}
                    >sessie starten</button>
                )}
            </span>

        </div>
    );
};

export default ProjectMetaStrip;
